import { DOCUMENT } from '@angular/common';
import { Component, EventEmitter, Inject, OnInit, Output, ViewChild } from '@angular/core';
import { FormArray, FormGroup, UntypedFormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { TranslocoService } from '@ngneat/transloco';
import { DialogConfirm } from 'app/dialogs/confirm/dialog-confirm';
import { DialogSettingsTranslationsAdd } from 'app/dialogs/settings/translations/add/dialog-settings-translations-add';
import { TranslocoHttpLoader } from 'app/fuse-template/core/transloco/transloco.http-loader';
import { DropDownItem } from 'app/interfaces/items/dropDownItem';
import { UpdateTranslationsReq } from 'app/interfaces/requests/Update/updateTranslationsReq';
import { ExposedReadUserDetailsRes } from 'app/interfaces/responses/Exposed/exposedReadUserDetailsRes';
import { ListTranslationConfigsRes } from 'app/interfaces/responses/List/listTranslationConfigsRes';
import { LsrauthService } from 'app/services/api/lsrauth/lsrauth.service';
import { TranslationService } from 'app/services/api/translation/translation.service';
import { USER_ROLES } from 'common/constants/user-role.constants';
import { differenceWith } from 'lodash-es';

@Component({
    selector: 'app-settings-translations',
    templateUrl: './settings-translations.component.html',
})
export class SettingsTranslationsComponent implements OnInit {
    @ViewChild(MatSort) sort: MatSort;
    @ViewChild('tablePaginator') displayedDataPaginator: MatPaginator;
    @Output() onHasPendingChanges = new EventEmitter<boolean>();
    response: ListTranslationConfigsRes[];
    allResponses: ListTranslationConfigsRes[];
    form: FormGroup;
    formArray: FormArray;
    dataSource: MatTableDataSource<any>;
    tableColumns: string[] = ['context', 'translationKey', 'translationValue', 'action'];
    pendingChanges: UpdateTranslationsReq[] = [];
    pendingChangesZero: UpdateTranslationsReq[] = [];
    pendingChangesOne: UpdateTranslationsReq[] = [];
    pendingChangesOther: UpdateTranslationsReq[] = [];
    userDetails: ExposedReadUserDetailsRes = null;
    userRoles = USER_ROLES;
    contextList: Array<DropDownItem> = [];
    langsList: Array<DropDownItem> = [];
    searchValue: string = '';

    constructor(
        @Inject(DOCUMENT) private _document: any,
        public lsrauthService: LsrauthService,
        private formBuilder: UntypedFormBuilder,
        public dialog: MatDialog,
        private translationService: TranslationService,
        private _fuseConfirmationService: FuseConfirmationService,
        private transloco: TranslocoHttpLoader,
        public translocoService: TranslocoService
    ) {
        this.lsrauthService.userDetails.subscribe((userDetails) => (this.userDetails = userDetails));

        this.form = this.formBuilder.group({
            rows: this.formBuilder.array([]),
        });

        this.formArray = this.form.get('rows') as FormArray;
    }

    ngOnInit(): void {
        this.refresh();
        this.getContexts();
        this.getLanguage();
    }

    //#region Get data
    refresh() {
        this.translationService.getTranslationConfigs().then((res) => {
            this.getContextTranslation(res);
            this.response = res;
            this.allResponses = res;
            this.initForm();
            this.searchFilter(this.searchValue);
        });
    }

    getContexts() {
        this.translationService.listTranslationContexts().subscribe((res) => {
            this.contextList = res.map((context) => {
                return {
                    value: context.contextId,
                    viewValue: context.contextName,
                } as DropDownItem;
            });
        });
    }

    getContextTranslation(res) {
        res.forEach((r) => {
            r.contextName = this.translationService.translate('label_translation_contexts_' + r.contextName);
        });
        return res;
    }

    getLanguage() {
        let availableLangs = this.translocoService.getAvailableLangs();
        this.langsList = availableLangs.map((lang) => {
            return {
                value: lang.id.toLowerCase(),
                viewValue: lang.label.toLowerCase(),
            } as DropDownItem;
        });
    }

    initForm(): void {
        this.form.setControl('rows', this.formBuilder.array([]));
        this.formArray = this.form.get('rows') as FormArray;
        this.response.forEach((item) => {
            this.formArray.push(
                this.formBuilder.group({
                    translationId: [item.translationId],
                    translationKey: [item.translationKey],
                    translationValue: [item.translationValue],
                    context: [item.contextName],
                    language: [item.language],
                    zero: [item.zero],
                    one: [item.one],
                    other: [item.other],
                })
            );
        });
        this.dataSource = new MatTableDataSource(this.formArray.controls);
        this.dataSource.sort = this.sort;
        this.sort.sortChange.subscribe((sort) => {
            this.applySort(sort);
        });
        this.dataSource.paginator = this.displayedDataPaginator;
        this.form.valueChanges.subscribe((val) => {
            this.pendingChanges = differenceWith(
                val.rows,
                this.response,
                (a: UpdateTranslationsReq, b: UpdateTranslationsReq) =>
                    a.translationValue == b.translationValue && a.translationId == b.translationId
            );
            this.onHasPendingChanges.emit(this.pendingChanges.length > 0);

            this.pendingChangesZero = differenceWith(
                val.rows,
                this.response,
                (a: UpdateTranslationsReq, b: UpdateTranslationsReq) => a.zero == b.zero && a.translationId == b.translationId
            );
            this.onHasPendingChanges.emit(this.pendingChangesZero.length > 0);

            this.pendingChangesOne = differenceWith(
                val.rows,
                this.response,
                (a: UpdateTranslationsReq, b: UpdateTranslationsReq) => a.one == b.one && a.translationId == b.translationId
            );
            this.onHasPendingChanges.emit(this.pendingChangesOne.length > 0);

            this.pendingChangesOther = differenceWith(
                val.rows,
                this.response,
                (a: UpdateTranslationsReq, b: UpdateTranslationsReq) => a.other == b.other && a.translationId == b.translationId
            );
            this.onHasPendingChanges.emit(this.pendingChangesOther.length > 0);
        });
    }

    //#endregion

    //#region Check and revert value changes
    checkHasChanges(id: number): boolean {
        return this.pendingChanges.find((item) => item.translationId == id) != null;
    }

    checkHasChangesZero(id: number): boolean {
        return this.pendingChangesZero.find((item) => item.translationId == id) != null;
    }

    checkHasChangesOne(id: number): boolean {
        return this.pendingChangesOne.find((item) => item.translationId == id) != null;
    }

    checkHasChangesOther(id: number): boolean {
        return this.pendingChangesOther.find((item) => item.translationId == id) != null;
    }

    getInitialValue(id: number, element: string): string {
        let value = '';
        if (element == 'value') {
            value = this.response.find((item) => item.translationId == id).translationValue;
        } else if (element == 'zero') {
            value = this.response.find((item) => item.translationId == id).zero;
        } else if (element == 'one') {
            value = this.response.find((item) => item.translationId == id).one;
        } else if (element == 'other') {
            value = this.response.find((item) => item.translationId == id).other;
        }

        if (value) {
            return this.translationService.translate('info_original_value', {
                value,
            });
        }
        return null;
    }

    // Resets all values to their original state from the api response
    resetValues(): void {
        this.pendingChanges = [];
        this.pendingChangesZero = [];
        this.pendingChangesOne = [];
        this.pendingChangesOther = [];
        this.formArray.controls.forEach((control) => {
            let resetValue = this.response.find((item) => item.translationId == control.get('translationId').value);
            control.setValue({
                translationId: resetValue.translationId,
                translationKey: resetValue.translationKey,
                translationValue: resetValue.translationValue,
                context: resetValue.contextName,
                language: resetValue.language,
                zero: resetValue.zero,
                one: resetValue.one,
                other: resetValue.other,
            });
        });
    }

    //#endregion

    //#region Save changes, delete translations, add new translations
    save() {
        const dialogRef = this.dialog.open(DialogConfirm, {
            autoFocus: false,
            restoreFocus: false,
            width: '450px',
            disableClose: true,
        });

        if (this.pendingChangesZero.length != 0) {
            this.updatePendingChanges(this.pendingChangesZero);
        }
        if (this.pendingChangesOne.length != 0) {
            this.updatePendingChanges(this.pendingChangesOne);
        }
        if (this.pendingChangesOther.length != 0) {
            this.updatePendingChanges(this.pendingChangesOther);
        }

        dialogRef.afterClosed().subscribe((shouldSave) => {
            if (shouldSave) {
                this.translationService.updateTranslations(this.pendingChanges).subscribe({
                    next: () => this.refresh(),
                });
            }
        });
    }

    updateCountString(zero: string, one: string, other: string) {
        if (zero != '') {
            return `{ count, plural, =0 { ${zero} } one { # ${one} } other { # ${other} }`;
        }
        return `{ count, plural, one { ${one} } other { ${other} } }`;
    }

    updatePendingChanges(pendingList) {
        pendingList.forEach((element) => {
            element.translationValue = this.updateCountString(element.zero, element.one, element.other);
            if (!this.pendingChanges.find((y) => y.translationId == element.translationId)) {
                this.pendingChanges.push(element);
            }
        });
    }

    deleteTranslation(deletedId) {
        let key = this.response.find((item) => item.translationId == deletedId).translationKey;
        let confirmationForm = this.formBuilder.group({
            title: 'Eyða þýðingum',
            message: this.translationService.translate('info_confirm_delete_translations_message', { key }),
            icon: {
                show: true,
                name: 'heroicons_solid:trash',
                color: 'primary',
            },
            actions: {
                confirm: {
                    show: true,
                    label: this.translationService.translate('label_confirm'),
                    color: 'primary',
                },
                cancel: {
                    show: true,
                    label: this.translationService.translate('label_cancel'),
                },
            },
            dismissible: true,
        });

        // Open the dialog and save the reference of it
        const dialogRef = this._fuseConfirmationService.open(confirmationForm.value);

        // Subscribe to afterClosed from the dialog reference
        dialogRef.afterClosed().subscribe((result) => {
            if (result == 'confirmed') {
                this.translationService
                    .deleteTranslation({
                        translationId: deletedId,
                    })
                    .subscribe((result) => {
                        this.refresh();
                    });
            }
        });
    }

    addSettings() {
        const dialogRef = this.dialog.open(DialogSettingsTranslationsAdd, {
            autoFocus: false,
            restoreFocus: false,
            width: '450px',
            disableClose: true,
            data: {
                contextData: this.contextList,
                languageData: this.langsList,
            },
        });

        dialogRef.afterClosed().subscribe((result) => {
            this.refresh();
        });
    }

    //#endregion

    //#region Search and filter data
    searchFilter(filterValue) {
        this.searchValue = filterValue;
        this.response = this.allResponses;
        if (filterValue?.length == 0) {
            this.initForm();
        } else if (filterValue) {
            filterValue = filterValue.toLowerCase().trim(); // Remove whitespace
            let filterKeyValue = this.response.filter((x) => x.translationKey.toLowerCase().trim().includes(filterValue));
            let filterValueValue = this.response.filter((x) => x.translationValue.toLowerCase().trim().includes(filterValue));
            let filterContextValue = this.response.filter((x) => x.contextName.toLowerCase().trim().includes(filterValue));
            let searchCombined = filterKeyValue
                .concat(filterValueValue)
                .concat(filterContextValue)
                .filter(function (elem, index, self) {
                    return index == self.indexOf(elem);
                });
            this.response = searchCombined;
            this.initForm();
        }
    }

    // Custom sort function because of some weird behaviour due to having form controls as the table data source
    applySort(sort: Sort) {
        let data = this.formArray.controls;

        let sortedData = data.sort((a, b) => {
            const valueA = a.get(sort.active).value;
            const valueB = b.get(sort.active).value;

            if (!sort.active || sort.direction === '') {
                const valueAIndex = this.response.findIndex((item) => item.translationKey == a.get('key').value);
                const valueBIndex = this.response.findIndex((item) => item.translationKey == b.get('key').value);
                return valueAIndex < valueBIndex ? -1 : 1;
            }

            if (valueA < valueB) {
                return sort.direction === 'asc' ? -1 : 1;
            } else if (valueA > valueB) {
                return sort.direction === 'asc' ? 1 : -1;
            } else {
                return 0;
            }
        });
        this.dataSource = new MatTableDataSource(sortedData);
        this.dataSource.paginator = this.displayedDataPaginator;
    }

    //#endregion
}
